/**
 * 平台超管域云函数 Web 端封装（add-platform-admin-web）。
 *
 * 与员工 `api/auth.ts` 完全独立：token 从平台会话（`utils/platform-session.ts`，
 * key `platform-token`）注入，经 `callFunction` 第三参数显式指定，绝不使用员工 token。
 * platform-login 是公开函数，显式传 `{ token: null }` 避免误带员工 token。
 */
import type {
  PlatformLoginInput,
  PlatformLoginOutput,
  PlatformLogoutOutput,
  PlatformMeOutput,
  PlatformOverviewInput,
  PlatformOverviewOutput,
  TenantCreateInput,
  TenantCreateOutput,
  TenantDetailInput,
  TenantDetailOutput,
  TenantListInput,
  TenantListOutput,
  TenantUpdateInput,
  TenantView,
  PlatformAuditListInput,
  PlatformAuditListOutput,
  PlatformCustomerContact,
  PlatformCustomerContactCreateInput,
  PlatformCustomerContactDeleteInput,
  PlatformCustomerContactUpdateInput,
  PlatformCustomerCreateInput,
  PlatformCustomerCreateOutput,
  PlatformCustomerDashboardInput,
  PlatformCustomerDashboardOutput,
  PlatformCustomerDetailInput,
  PlatformCustomerDetailOutput,
  PlatformCustomerFollowUp,
  PlatformCustomerFollowUpCreateInput,
  PlatformCustomerFollowUpListInput,
  PlatformCustomerFollowUpListOutput,
  PlatformCustomerLinkTenantInput,
  PlatformCustomerListInput,
  PlatformCustomerListOutput,
  PlatformCustomerOwnerHistoryListInput,
  PlatformCustomerOwnerHistoryListOutput,
  PlatformCustomerTransferOwnerInput,
  PlatformCustomerUpdateInput,
  PlatformPerson,
  PlatformPersonCreateInput,
  PlatformPersonCreateOutput,
  PlatformPersonListInput,
  PlatformPersonListOutput,
  PlatformPersonResetPasswordInput,
  PlatformPersonResetPasswordOutput,
  PlatformPersonUpdateInput,
  PlatformRole,
  PlatformRoleListInput,
  PlatformRoleListOutput,
  PlatformRoleUpdateInput,
  PlatformTeam,
  PlatformTeamCreateInput,
  PlatformTeamListInput,
  PlatformTeamListOutput,
  PlatformTeamUpdateInput,
  PlatformCatalogOptionsInput,
  PlatformCatalogOptionsOutput,
  PlatformCatalogPrice,
  PlatformPackage,
  PlatformPackageCreateInput,
  PlatformPackageDetailOutput,
  PlatformPackageListInput,
  PlatformPackageListOutput,
  PlatformPackagePriceCreateInput,
  PlatformPackagePriceListInput,
  PlatformPackagePriceListOutput,
  PlatformPackageUpdateInput,
  PlatformPackageVersion,
  PlatformPackageVersionCreateInput,
  PlatformPackageVersionPublishInput,
  PlatformProduct,
  PlatformProductCreateInput,
  PlatformProductDetailInput,
  PlatformProductListInput,
  PlatformProductListOutput,
  PlatformProductPriceCreateInput,
  PlatformProductPriceListInput,
  PlatformProductPriceListOutput,
  PlatformProductUpdateInput,
  PlatformEntitlementLedgerListOutput,
  PlatformEntitlementProjectionListOutput,
  PlatformEntitlementQueryInput,
  PlatformSalesOrder,
  PlatformSalesOrderCreateInput,
  PlatformSalesOrderDetailOutput,
  PlatformSalesOrderListInput,
  PlatformSalesOrderListOutput,
  PlatformSalesOrderPreviewInput,
  PlatformSalesOrderPreviewOutput,
  PlatformSalesOrderTransitionInput,
  PlatformSalesOrderConfirmPaymentInput,
  PlatformSalesOrderConfirmPaymentOutput,
  PlatformBillingReversalInput,
  PlatformBillingReversalOutput,
  PlatformOfflineRefundInput,
  PlatformOfflineRefundOutput,
  PlatformSalesOrderUpdateInput,
  PlatformLifecycleDashboardInput,
  PlatformLifecycleDashboardOutput,
  PlatformLifecycleListInput,
  PlatformLifecycleListOutput,
  PlatformLifecycleDetailInput,
  PlatformLifecycleDetailOutput,
  PlatformRenewalReminderListInput,
  PlatformRenewalReminderListOutput,
  PlatformRenewalTask,
  PlatformRenewalTaskDetailInput,
  PlatformRenewalTaskStartInput,
  PlatformRenewalTaskLinkOrderInput,
  PlatformRenewalTaskListInput,
  PlatformRenewalTaskListOutput,
  PlatformAuditDetailInput,
  PlatformAuditExportInput,
  PlatformAuditExportOutput,
  PlatformChangePasswordInput,
  PlatformChangePasswordOutput,
  PlatformReauthInput,
  PlatformReauthOutput,
  PlatformSecurityEventListInput,
  PlatformSecurityEventListOutput,
  PlatformSessionListOutput,
  PlatformSessionRevokeInput,
  PlatformSessionRevokeOutput,
  PlatformReauthProofInput,
  PlatformAuditLog,
  PlatformEnterpriseServiceDetailInput,
  PlatformEnterpriseServiceDetailOutput,
  PlatformEnterpriseAccountsListInput,
  PlatformEnterpriseAccountsListOutput,
  PlatformEnterpriseAdminPhoneChangeInput,
  PlatformEnterpriseAdminPhoneChangeOutput,
  PlatformBillingCreditOptionsInput,
  PlatformBillingCreditOptionsOutput,
} from '@cms/shared'
import { callFunction } from '../utils/tcb'
import { getPlatformToken } from '../utils/platform-session'

/** 平台 token：无会话时为 null（调用方决定是否注入）。 */
function platformToken(): string | null {
  return getPlatformToken()
}

export async function platformLogin(input: PlatformLoginInput): Promise<PlatformLoginOutput> {
  return callFunction<PlatformLoginOutput>(
    'platform-login',
    {
      data: input as unknown as Record<string, unknown>,
    },
    { token: null },
  )
}

export async function platformMe(): Promise<PlatformMeOutput> {
  return callFunction<PlatformMeOutput>(
    'platform-me',
    {
      data: {},
    },
    { token: platformToken() },
  )
}

export async function platformLogout(): Promise<PlatformLogoutOutput> {
  return callFunction<PlatformLogoutOutput>(
    'platform-logout',
    {
      data: {},
    },
    { token: platformToken() },
  )
}

export async function listTenants(input: TenantListInput): Promise<TenantListOutput> {
  return callFunction<TenantListOutput>(
    'tenant-list',
    {
      data: input as unknown as Record<string, unknown>,
    },
    { token: platformToken() },
  )
}

export async function createTenant(input: TenantCreateInput): Promise<TenantCreateOutput> {
  return callFunction<TenantCreateOutput>(
    'tenant-create',
    {
      data: input as unknown as Record<string, unknown>,
    },
    { token: platformToken() },
  )
}

export async function updateTenant(input: TenantUpdateInput): Promise<TenantView> {
  return callFunction<TenantView>(
    'tenant-update',
    {
      data: input as unknown as Record<string, unknown>,
    },
    { token: platformToken() },
  )
}

export async function getTenantDetail(input: TenantDetailInput): Promise<TenantDetailOutput> {
  return callFunction<TenantDetailOutput>(
    'tenant-detail',
    {
      data: input as unknown as Record<string, unknown>,
    },
    { token: platformToken() },
  )
}

export async function getPlatformOverview(
  input: PlatformOverviewInput,
): Promise<PlatformOverviewOutput> {
  return callFunction<PlatformOverviewOutput>(
    'platform-overview',
    {
      data: input as unknown as Record<string, unknown>,
    },
    { token: platformToken() },
  )
}

function platformCall<T>(name: string, input: object): Promise<T> {
  return callFunction<T>(
    name,
    { data: input as Record<string, unknown> },
    { token: platformToken() },
  )
}

export const listPlatformPeople = (
  input: PlatformPersonListInput,
): Promise<PlatformPersonListOutput> => platformCall('platform-person-list', input)
export const createPlatformPerson = (
  input: PlatformPersonCreateInput,
): Promise<PlatformPersonCreateOutput> => platformCall('platform-person-create', input)
export const updatePlatformPerson = (input: PlatformPersonUpdateInput): Promise<PlatformPerson> =>
  platformCall('platform-person-update', input)
export const resetPlatformPersonPassword = (
  input: PlatformPersonResetPasswordInput,
): Promise<PlatformPersonResetPasswordOutput> =>
  platformCall('platform-person-reset-password', input)
export const listPlatformRoles = (input: PlatformRoleListInput): Promise<PlatformRoleListOutput> =>
  platformCall('platform-role-list', input)
export const updatePlatformRole = (input: PlatformRoleUpdateInput): Promise<PlatformRole> =>
  platformCall('platform-role-update', input)
export const listPlatformTeams = (input: PlatformTeamListInput): Promise<PlatformTeamListOutput> =>
  platformCall('platform-team-list', input)
export const createPlatformTeam = (input: PlatformTeamCreateInput): Promise<PlatformTeam> =>
  platformCall('platform-team-create', input)
export const updatePlatformTeam = (input: PlatformTeamUpdateInput): Promise<PlatformTeam> =>
  platformCall('platform-team-update', input)
export const listPlatformCustomers = (
  input: PlatformCustomerListInput,
): Promise<PlatformCustomerListOutput> => platformCall('platform-customer-list', input)
export const createPlatformCustomer = (
  input: PlatformCustomerCreateInput,
): Promise<PlatformCustomerCreateOutput> => platformCall('platform-customer-create', input)
export const updatePlatformCustomer = (
  input: PlatformCustomerUpdateInput,
): Promise<PlatformCustomerDetailOutput> => platformCall('platform-customer-update', input)
export const getPlatformCustomerDetail = (
  input: PlatformCustomerDetailInput,
): Promise<PlatformCustomerDetailOutput> => platformCall('platform-customer-detail', input)
export const linkPlatformCustomerTenant = (
  input: PlatformCustomerLinkTenantInput,
): Promise<PlatformCustomerDetailOutput> => platformCall('platform-customer-link-tenant', input)
export const createPlatformCustomerContact = (
  input: PlatformCustomerContactCreateInput,
): Promise<PlatformCustomerContact> => platformCall('platform-customer-contact-create', input)
export const updatePlatformCustomerContact = (
  input: PlatformCustomerContactUpdateInput,
): Promise<PlatformCustomerContact> => platformCall('platform-customer-contact-update', input)
export const deletePlatformCustomerContact = (
  input: PlatformCustomerContactDeleteInput,
): Promise<{ deleted: true }> => platformCall('platform-customer-contact-delete', input)
export const listPlatformCustomerFollowUps = (
  input: PlatformCustomerFollowUpListInput,
): Promise<PlatformCustomerFollowUpListOutput> =>
  platformCall('platform-customer-follow-up-list', input)
export const createPlatformCustomerFollowUp = (
  input: PlatformCustomerFollowUpCreateInput,
): Promise<PlatformCustomerFollowUp> => platformCall('platform-customer-follow-up-create', input)
export const transferPlatformCustomerOwner = (
  input: PlatformCustomerTransferOwnerInput,
): Promise<PlatformCustomerDetailOutput> => platformCall('platform-customer-transfer-owner', input)
export const listPlatformCustomerOwnerHistory = (
  input: PlatformCustomerOwnerHistoryListInput,
): Promise<PlatformCustomerOwnerHistoryListOutput> =>
  platformCall('platform-customer-owner-history-list', input)
export const getPlatformCustomerDashboard = (
  input: PlatformCustomerDashboardInput,
): Promise<PlatformCustomerDashboardOutput> => platformCall('platform-customer-dashboard', input)
export const listPlatformAuditLogs = (
  input: PlatformAuditListInput,
): Promise<PlatformAuditListOutput> => platformCall('platform-audit-list', input)
export const getPlatformAuditLog = (input: PlatformAuditDetailInput): Promise<PlatformAuditLog> =>
  platformCall('platform-audit-detail', input)
export const exportPlatformAuditLogs = (
  input: PlatformAuditExportInput,
): Promise<PlatformAuditExportOutput> => platformCall('platform-audit-export', input)
export const changePlatformPassword = (
  input: PlatformChangePasswordInput,
): Promise<PlatformChangePasswordOutput> => platformCall('platform-change-password', input)
export const reauthenticatePlatform = (input: PlatformReauthInput): Promise<PlatformReauthOutput> =>
  platformCall('platform-reauth', input)
export const listPlatformSessions = (): Promise<PlatformSessionListOutput> =>
  platformCall('platform-session-list', {})
export const revokePlatformSession = (
  input: PlatformSessionRevokeInput,
): Promise<PlatformSessionRevokeOutput> => platformCall('platform-session-revoke', input)
export const revokeOtherPlatformSessions = (
  input: PlatformReauthProofInput,
): Promise<PlatformSessionRevokeOutput> => platformCall('platform-session-revoke-others', input)
export const listPlatformSecurityEvents = (
  input: PlatformSecurityEventListInput,
): Promise<PlatformSecurityEventListOutput> => platformCall('platform-security-event-list', input)
export const listPlatformProducts = (
  input: PlatformProductListInput,
): Promise<PlatformProductListOutput> => platformCall('platform-product-list', input)
export const createPlatformProduct = (
  input: PlatformProductCreateInput,
): Promise<PlatformProduct> => platformCall('platform-product-create', input)
export const updatePlatformProduct = (
  input: PlatformProductUpdateInput,
): Promise<PlatformProduct> => platformCall('platform-product-update', input)
export const getPlatformProductDetail = (
  input: PlatformProductDetailInput,
): Promise<PlatformProduct> => platformCall('platform-product-detail', input)
export const listPlatformProductPrices = (
  input: PlatformProductPriceListInput,
): Promise<PlatformProductPriceListOutput> => platformCall('platform-product-price-list', input)
export const createPlatformProductPrice = (
  input: PlatformProductPriceCreateInput,
): Promise<PlatformCatalogPrice> => platformCall('platform-product-price-create', input)
export const listPlatformPackages = (
  input: PlatformPackageListInput,
): Promise<PlatformPackageListOutput> => platformCall('platform-package-list', input)
export const createPlatformPackage = (
  input: PlatformPackageCreateInput,
): Promise<PlatformPackage> => platformCall('platform-package-create', input)
export const updatePlatformPackage = (
  input: PlatformPackageUpdateInput,
): Promise<PlatformPackage> => platformCall('platform-package-update', input)
export const getPlatformPackageDetail = (input: {
  id: string
}): Promise<PlatformPackageDetailOutput> => platformCall('platform-package-detail', input)
export const createPlatformPackageVersion = (
  input: PlatformPackageVersionCreateInput,
): Promise<PlatformPackageVersion> => platformCall('platform-package-version-create', input)
export const publishPlatformPackageVersion = (
  input: PlatformPackageVersionPublishInput,
): Promise<PlatformPackageVersion> => platformCall('platform-package-version-publish', input)
export const listPlatformPackagePrices = (
  input: PlatformPackagePriceListInput,
): Promise<PlatformPackagePriceListOutput> => platformCall('platform-package-price-list', input)
export const createPlatformPackagePrice = (
  input: PlatformPackagePriceCreateInput,
): Promise<PlatformCatalogPrice> => platformCall('platform-package-price-create', input)
export const getPlatformCatalogOptions = (
  input: PlatformCatalogOptionsInput,
): Promise<PlatformCatalogOptionsOutput> => platformCall('platform-catalog-options', input)

export const listPlatformSalesOrders = (
  input: PlatformSalesOrderListInput,
): Promise<PlatformSalesOrderListOutput> => platformCall('platform-sales-order-list', input)
export const getPlatformSalesOrderDetail = (input: {
  id: string
}): Promise<PlatformSalesOrderDetailOutput> => platformCall('platform-sales-order-detail', input)
export const createPlatformSalesOrder = (
  input: PlatformSalesOrderCreateInput,
): Promise<PlatformSalesOrder> => platformCall('platform-sales-order-create', input)
export const updatePlatformSalesOrder = (
  input: PlatformSalesOrderUpdateInput,
): Promise<PlatformSalesOrder> => platformCall('platform-sales-order-update', input)
const transitionPlatformSalesOrder = (
  action: string,
  input: PlatformSalesOrderTransitionInput,
): Promise<PlatformSalesOrder> => platformCall(`platform-sales-order-${action}`, input)
export const submitPlatformSalesOrder = (input: PlatformSalesOrderTransitionInput) =>
  transitionPlatformSalesOrder('submit', input)
export const approvePlatformSalesOrder = (input: PlatformSalesOrderTransitionInput) =>
  transitionPlatformSalesOrder('approve', input)
export const markPaidPlatformSalesOrder = (input: PlatformSalesOrderTransitionInput) =>
  transitionPlatformSalesOrder('mark-paid', input)
/** 确认本次新增实收并原子生效订单；同一次重试必须复用 idempotencyKey。 */
export const confirmPlatformSalesOrderPayment = (
  input: PlatformSalesOrderConfirmPaymentInput,
): Promise<PlatformSalesOrderConfirmPaymentOutput> =>
  platformCall('platform-sales-order-mark-paid', input)
/** 查询当前目标企业可用于转抵的真实收款余额；时间窗与分页由页面显式提供。 */
export const listPlatformBillingCreditOptions = (
  input: PlatformBillingCreditOptionsInput,
): Promise<PlatformBillingCreditOptionsOutput> =>
  platformCall('platform-billing-credit-options', input)
export const effectPlatformSalesOrder = (input: PlatformSalesOrderTransitionInput) =>
  transitionPlatformSalesOrder('effect', input)
export const cancelPlatformSalesOrder = (input: PlatformSalesOrderTransitionInput) =>
  transitionPlatformSalesOrder('cancel', input)
export const reversePlatformSalesOrder = (input: PlatformSalesOrderTransitionInput) =>
  transitionPlatformSalesOrder('reverse', input)
export const listPlatformEntitlementProjections = (
  input: PlatformEntitlementQueryInput,
): Promise<PlatformEntitlementProjectionListOutput> =>
  platformCall('platform-entitlement-projection-list', input)
export const listPlatformEntitlementLedger = (
  input: PlatformEntitlementQueryInput,
): Promise<PlatformEntitlementLedgerListOutput> =>
  platformCall('platform-entitlement-ledger-list', input)
export const previewPlatformSalesOrderEffect = (
  input: PlatformSalesOrderPreviewInput,
): Promise<PlatformSalesOrderPreviewOutput> =>
  platformCall('platform-sales-order-effect-preview', input)
export const previewPlatformSalesOrderReverse = (
  input: PlatformSalesOrderPreviewInput,
): Promise<PlatformSalesOrderPreviewOutput> =>
  platformCall('platform-sales-order-reverse-preview', input)
/** 对已收款生效订单执行整单冲账。 */
export const confirmPlatformBillingReversal = (
  input: PlatformBillingReversalInput,
): Promise<PlatformBillingReversalOutput> => platformCall('platform-billing-reversal-confirm', input)
/** 登记已经在线下完成的退款事实，不发起在线退款。 */
export const recordPlatformOfflineRefund = (
  input: PlatformOfflineRefundInput,
): Promise<PlatformOfflineRefundOutput> => platformCall('platform-offline-refund-record', input)
export const reauthenticatePlatformOperation = (input: PlatformReauthInput): Promise<PlatformReauthOutput> =>
  platformCall('platform-reauth', input)

/** 读取企业真实服务构成与授权期间。 */
export const getPlatformEnterpriseServiceDetail = (
  input: PlatformEnterpriseServiceDetailInput,
): Promise<PlatformEnterpriseServiceDetailOutput> =>
  platformCall('platform-enterprise-service-detail', input)

/** 分页读取平台可见的企业内部账号摘要。 */
export const listPlatformEnterpriseAccounts = (
  input: PlatformEnterpriseAccountsListInput,
): Promise<PlatformEnterpriseAccountsListOutput> =>
  platformCall('platform-enterprise-accounts-list', input)

/** 超级管理员经绑定再认证后更正企业管理员手机号。 */
export const changePlatformEnterpriseAdminPhone = (
  input: PlatformEnterpriseAdminPhoneChangeInput,
): Promise<PlatformEnterpriseAdminPhoneChangeOutput> =>
  platformCall('platform-enterprise-admin-phone-change', input)

export const getPlatformLifecycleDashboard = (
  input: PlatformLifecycleDashboardInput,
): Promise<PlatformLifecycleDashboardOutput> => platformCall('platform-lifecycle-dashboard', input)
export const listPlatformLifecycle = (
  input: PlatformLifecycleListInput,
): Promise<PlatformLifecycleListOutput> => platformCall('platform-lifecycle-list', input)
export const getPlatformLifecycleDetail = (
  input: PlatformLifecycleDetailInput,
): Promise<PlatformLifecycleDetailOutput> => platformCall('platform-lifecycle-detail', input)
export const listPlatformRenewalReminders = (
  input: PlatformRenewalReminderListInput,
): Promise<PlatformRenewalReminderListOutput> =>
  platformCall('platform-renewal-reminder-list', input)
export const listPlatformRenewalTasks = (
  input: PlatformRenewalTaskListInput,
): Promise<PlatformRenewalTaskListOutput> => platformCall('platform-renewal-task-list', input)
export const getPlatformRenewalTaskDetail = (
  input: PlatformRenewalTaskDetailInput,
): Promise<PlatformRenewalTask> => platformCall('platform-renewal-task-detail', input)
export const startPlatformRenewalTask = (
  input: PlatformRenewalTaskStartInput,
): Promise<PlatformRenewalTask> => platformCall('platform-renewal-task-start', input)
export const linkPlatformRenewalTaskOrder = (
  input: PlatformRenewalTaskLinkOrderInput,
): Promise<PlatformRenewalTask> => platformCall('platform-renewal-task-link-order', input)
